import { forwardRef, useState, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';

// Printable Missing Person Flyer Component
const MissingPersonFlyer = forwardRef(({ caseData, contactInfo, qrOptions, photoOptions, ageProgressions }, ref) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  // Handle image loading - convert to data URL if needed
  useEffect(() => {
    const loadImage = async () => {
      if (caseData.photo_base64) {
        // Already have base64, use it directly
        const src = caseData.photo_base64.startsWith('data:') 
          ? caseData.photo_base64 
          : `data:image/jpeg;base64,${caseData.photo_base64}`;
        setImageSrc(src);
        setImageLoaded(true);
      } else if (caseData.photo_url) {
        // Try to fetch and convert to base64 for reliable printing
        try {
          const response = await fetch(caseData.photo_url);
          const blob = await response.blob();
          const reader = new FileReader();
          reader.onloadend = () => {
            setImageSrc(reader.result);
            setImageLoaded(true);
          };
          reader.readAsDataURL(blob);
        } catch (error) {
          console.error('Failed to load image:', error);
          // Fallback to direct URL
          setImageSrc(caseData.photo_url);
          setImageLoaded(true);
        }
      } else {
        setImageLoaded(true);
      }
    };

    loadImage();
  }, [caseData.photo_base64, caseData.photo_url]);

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const getBaseUrl = () => window.location.origin;
  const getCaseUrl = () => `${getBaseUrl()}/case/${caseData?.id}`;
  const getTipUrl = () => `${getBaseUrl()}/tip/${caseData?.id}`;

  const showQr = qrOptions?.showQrCode || qrOptions?.showTipQr;
  
  // Age progression image handling
  const showAgeProg = photoOptions?.showAgeProgression && ageProgressions && ageProgressions.length > 0;
  const selectedProgression = showAgeProg ? ageProgressions[photoOptions?.selectedProgressionIdx || 0] : null;
  const ageProgImgSrc = selectedProgression?.image_url || selectedProgression?.image_base64 || null;

  return (
    <div
      ref={ref}
      className="flyer-container"
      style={{
        width: '8.5in',
        minHeight: '11in',
        padding: '0.4in',
        fontFamily: 'Arial, Helvetica, sans-serif',
        boxSizing: 'border-box',
        backgroundColor: '#ffffff',
        color: '#000000',
      }}
    >
      {/* Header - MISSING Banner */}
      <div
        style={{
          backgroundColor: '#dc2626',
          color: '#ffffff',
          padding: '20px',
          textAlign: 'center',
          marginBottom: '16px',
          WebkitPrintColorAdjust: 'exact',
          printColorAdjust: 'exact',
        }}
      >
        <h1 style={{ 
          fontSize: '52px', 
          fontWeight: 'bold', 
          margin: 0, 
          letterSpacing: '12px',
          textTransform: 'uppercase'
        }}>
          MISSING
        </h1>
      </div>

      {/* Main Content - Photo and Info Side by Side */}
      <div style={{ display: 'flex', gap: '20px', marginBottom: '16px' }}>
        {/* Photo Section */}
        <div style={{ flex: '0 0 260px' }}>
          {imageSrc ? (
            <img
              src={imageSrc}
              alt={caseData.name || 'Missing Person'}
              style={{
                width: '260px',
                height: '320px',
                objectFit: 'cover',
                border: '3px solid #1f2937',
                display: 'block',
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div
            style={{
              width: '260px',
              height: '320px',
              backgroundColor: '#d1d5db',
              display: imageSrc ? 'none' : 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '3px solid #1f2937',
              fontSize: '72px',
              fontWeight: 'bold',
              color: '#6b7280',
            }}
          >
            {getInitials(caseData.name)}
          </div>
          
          {/* Age Progression Section */}
          {showAgeProg && ageProgImgSrc && (
            <div style={{ marginTop: '12px', textAlign: 'center' }}>
              <img
                src={ageProgImgSrc}
                alt="Age Progression"
                style={{
                  width: '200px',
                  height: '200px',
                  objectFit: 'cover',
                  border: '3px solid #7c3aed',
                  borderRadius: '8px',
                  display: 'block',
                  margin: '0 auto',
                }}
              />
              <div style={{ 
                fontSize: '11px', 
                fontWeight: 'bold', 
                color: '#7c3aed', 
                marginTop: '6px',
                textTransform: 'uppercase'
              }}>
                🔮 Age Progression
              </div>
              <div style={{ fontSize: '10px', color: '#6b7280', marginTop: '2px' }}>
                +{selectedProgression?.years_progressed || 'N'} years (AI-generated)
              </div>
            </div>
          )}
        </div>

        {/* Info Section */}
        <div style={{ flex: 1 }}>
          <h2 style={{ 
            fontSize: '28px', 
            fontWeight: 'bold', 
            margin: '0 0 12px 0', 
            color: '#111827',
            borderBottom: '2px solid #111827',
            paddingBottom: '8px'
          }}>
            {caseData.name || 'Unknown'}
          </h2>
          
          <table style={{ width: '100%', fontSize: '16px', borderCollapse: 'collapse' }}>
            <tbody>
              {caseData.age && (
                <tr>
                  <td style={{ padding: '4px 8px 4px 0', fontWeight: 'bold', width: '100px' }}>Age:</td>
                  <td style={{ padding: '4px 0' }}>{caseData.age} years old</td>
                </tr>
              )}
              {caseData.gender && (
                <tr>
                  <td style={{ padding: '4px 8px 4px 0', fontWeight: 'bold' }}>Gender:</td>
                  <td style={{ padding: '4px 0' }}>{caseData.gender}</td>
                </tr>
              )}
              {caseData.race && (
                <tr>
                  <td style={{ padding: '4px 8px 4px 0', fontWeight: 'bold' }}>Race:</td>
                  <td style={{ padding: '4px 0' }}>{caseData.race}</td>
                </tr>
              )}
              {(caseData.height || caseData.weight) && (
                <tr>
                  <td style={{ padding: '4px 8px 4px 0', fontWeight: 'bold' }}>Build:</td>
                  <td style={{ padding: '4px 0' }}>{[caseData.height, caseData.weight].filter(Boolean).join(', ')}</td>
                </tr>
              )}
              {caseData.hair_color && (
                <tr>
                  <td style={{ padding: '4px 8px 4px 0', fontWeight: 'bold' }}>Hair:</td>
                  <td style={{ padding: '4px 0' }}>{caseData.hair_color}</td>
                </tr>
              )}
              {caseData.eye_color && (
                <tr>
                  <td style={{ padding: '4px 8px 4px 0', fontWeight: 'bold' }}>Eyes:</td>
                  <td style={{ padding: '4px 0' }}>{caseData.eye_color}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Last Seen Section */}
      <div
        style={{
          backgroundColor: '#fef3c7',
          border: '2px solid #d97706',
          padding: '12px 16px',
          marginBottom: '12px',
          WebkitPrintColorAdjust: 'exact',
          printColorAdjust: 'exact',
        }}
      >
        <h3 style={{ 
          fontSize: '20px', 
          fontWeight: 'bold', 
          margin: '0 0 8px 0', 
          color: '#92400e' 
        }}>
          📍 LAST SEEN
        </h3>
        <p style={{ fontSize: '16px', margin: '4px 0' }}>
          <strong>Location:</strong> {caseData.last_seen_location || caseData.state || 'Unknown'}
        </p>
        <p style={{ fontSize: '16px', margin: '4px 0' }}>
          <strong>Date:</strong> {formatDate(caseData.last_seen_date || caseData.missing_since)}
        </p>
      </div>

      {/* Description */}
      {(caseData.description || caseData.distinguishing_features || caseData.physical_features) && (
        <div style={{ marginBottom: '12px' }}>
          <h3 style={{ 
            fontSize: '18px', 
            fontWeight: 'bold', 
            margin: '0 0 8px 0', 
            color: '#111827',
            borderBottom: '1px solid #d1d5db',
            paddingBottom: '4px'
          }}>
            DESCRIPTION
          </h3>
          {caseData.description && (
            <p style={{ fontSize: '14px', margin: '4px 0', lineHeight: '1.4', color: '#374151' }}>
              {caseData.description}
            </p>
          )}
          {(caseData.distinguishing_features || caseData.physical_features) && (
            <p style={{ fontSize: '14px', margin: '8px 0', color: '#374151' }}>
              <strong>Distinguishing Features:</strong> {caseData.distinguishing_features || caseData.physical_features}
            </p>
          )}
        </div>
      )}

      {/* Contact Section */}
      <div
        style={{
          backgroundColor: '#1f2937',
          color: '#ffffff',
          padding: '16px',
          marginBottom: '12px',
          textAlign: 'center',
          WebkitPrintColorAdjust: 'exact',
          printColorAdjust: 'exact',
        }}
      >
        <h3 style={{ fontSize: '18px', fontWeight: 'bold', margin: '0 0 8px 0' }}>
          IF YOU HAVE ANY INFORMATION, PLEASE CONTACT:
        </h3>
        
        <div style={{ fontSize: '28px', fontWeight: 'bold', margin: '8px 0' }}>
          {contactInfo?.phone || '911 or Local Police'}
        </div>
        
        {contactInfo?.tip_line && (
          <div style={{ fontSize: '18px', margin: '4px 0' }}>
            Tip Line: {contactInfo.tip_line}
          </div>
        )}

        <div style={{ fontSize: '14px', marginTop: '8px', opacity: 0.9 }}>
          National Center for Missing & Exploited Children: <strong>1-800-THE-LOST (1-800-843-5678)</strong>
        </div>
      </div>

      {/* QR Code Section */}
      {showQr && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '40px',
            margin: '16px 0',
            padding: '16px',
            backgroundColor: '#f8fafc',
            border: '1px solid #e2e8f0',
            borderRadius: '8px',
            WebkitPrintColorAdjust: 'exact',
            printColorAdjust: 'exact',
          }}
        >
          {qrOptions?.showQrCode && (
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  background: 'white',
                  padding: '8px',
                  border: '2px solid #1f2937',
                  borderRadius: '4px',
                  display: 'inline-block',
                }}
              >
                <QRCodeSVG value={getCaseUrl()} size={80} level="M" />
              </div>
              <div style={{ fontSize: '11px', fontWeight: 'bold', marginTop: '6px', color: '#1f2937' }}>
                📱 SCAN FOR DETAILS
              </div>
              <div style={{ fontSize: '10px', color: '#6b7280' }}>
                View full case info
              </div>
            </div>
          )}
          {qrOptions?.showTipQr && (
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  background: 'white',
                  padding: '8px',
                  border: '2px solid #1f2937',
                  borderRadius: '4px',
                  display: 'inline-block',
                }}
              >
                <QRCodeSVG value={getTipUrl()} size={80} level="M" />
              </div>
              <div style={{ fontSize: '11px', fontWeight: 'bold', marginTop: '6px', color: '#1f2937' }}>
                💡 SUBMIT A TIP
              </div>
              <div style={{ fontSize: '10px', color: '#6b7280' }}>
                Anonymous tips accepted
              </div>
            </div>
          )}
        </div>
      )}

      {/* Footer */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        borderTop: '1px solid #d1d5db', 
        paddingTop: '8px',
        fontSize: '12px',
        color: '#6b7280'
      }}>
        <div>
          {caseData.source_name && <span>Source: {caseData.source_name}</span>}
          {caseData.external_id && (
            <span style={{ marginLeft: '12px' }}>
              Case #: {caseData.external_id.replace('namus_', '')}
            </span>
          )}
        </div>
        <div>
          Generated by LinkTheMissing.com
        </div>
      </div>

      {/* Case URL */}
      <div style={{ textAlign: 'center', marginTop: '8px', fontSize: '12px', color: '#6b7280' }}>
        View full case details at: <strong>linkthemissing.com/case/{caseData.id}</strong>
      </div>
    </div>
  );
});

MissingPersonFlyer.displayName = 'MissingPersonFlyer';

export default MissingPersonFlyer;
